// src/views/MenageIncidentsView.jsx — LOT Incidents 13 (statuts canoniques)
//
// Vue partagée rôle 'menage' (SES signalements, lecture) et 'orchestrateur'
// (incidents de SON pool, jugement + gestes).
//
// MÉCANIQUE (inchangée, validée Dimitri 2026-08-21) :
//   · l'AGENT DÉCLARE seulement (MenageTodayView, ≥2 photos) puis LIT ;
//   · l'ORCHESTRATEUR juge PAR DÉGRADATION (Récupérable / Non récupérable),
//     le STATUS global est DÉRIVÉ SERVEUR (≥1 « Non récupérable » → a_racheter,
//     sinon → resolu) — pas de raccourci global (décision Dimitri) ;
//   · rachat / dépôt = DEUX DATES, JAMAIS UN MONTANT (rachatDepotOrchestrateur) ;
//   · la clôture a_racheter → resolu est réservée à l'ADMIN.
//
// DESSIN ORCHESTRATEUR (maquette validée Dimitri 2026-08-21) :
//   · LE BANDEAU PORTE ENFIN QUELQUE CHOSE — même langage que le bandeau du
//     Suivi : « Incidents » 20px + « de mon équipe » 11.5, puis TROIS BLOCS
//     CLIQUABLES (fond blanc .13, r10, ≥44px ; pastille 30 r9 icône blanche
//     16 ; compte 14 blanc gras, précision 10 dessous). Le clic FILTRE la
//     liste ; second clic, le filtre se retire (actif = .22 + bord .3).
//     La petite ligne grise (resumeAttente) a DISPARU chez l'orchestrateur —
//     son information vit dans les blocs. L'agent la garde.
//   · UN SEUL MOT PAR ÉTAT : « À juger » partout (groupe, carte, bandeau) —
//     règle du dessin unique appliquée au langage. « Réglé » de même.
//   · LA CARTE : en-tête AUX COULEURS DE L'ÉTAT (dégradé 100deg + bordure +
//     pastille 34px icône blanche), nom du logement 15 gras, CONTEXTE sur une
//     ligne (« Claire Dubois · ménage de Sofia · 12 août »), ancienneté en
//     pastille blanche bordée (file de jugement seulement — sur les archives,
//     un compteur serait un reproche sans objet, doctrine 2026-08-06).
//   · LES PHOTOS SONT DES VIGNETTES 48px (c'est sur les photos qu'on juge —
//     un compteur ne sert à rien) ; la 3e porte « +N » sous voile quand il y
//     en a plus. Signées au MONTAGE de la carte (1 appel par incident à
//     photos) ; clic → visionneuse plein écran.
//   · UNE LIGNE PAR ARTICLE : puce 8px terracotta, type × N (12.5), précision
//     italique #a8664f, boutons carrés 42px à droite (Récupérable recyclage /
//     Non récupérable poubelle) ; filet #f2f4f6 entre les lignes.
//   · LA PARADE CONTRE L'ERREUR : icônes nues → APPUI LONG affiche le mot ;
//     et DÈS QU'UN ARTICLE EST JUGÉ, son état s'écrit EN TOUTES LETTRES à la
//     place des boutons (un jugement engage un rachat — il doit se VOIR).
//
// C-1 (2026-08-21) : « par … » des horodatages = PRÉNOM DÉRIVÉ
// (firstNameFromEmail), plus jamais l'identifiant brut.
//
// Q2 — pas de bouton « + Nouveau » : le signalement se fait depuis la carte
// du ménage (jour même ou fenêtre 48 h) ; au-delà : contact admin hors-app.

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { DS } from "../lib/ui"
import CarteTitrePage from '../components/CarteTitrePage'
// Ancienneté d'attente : sur une file de JUGEMENT, le plus vieux est le plus
// urgent — l'inverse de l'ordre naturel d'un journal.
import { trierParAnciennete, resumeAttente, attenteAnormale, joursDAttente } from "../lib/incidents-attente"
import { formatDateFR } from "../lib/format"
import {
  STATUS,
  normalizeStatus,
  fetchOrchestratorPoolIncidents,
  fetchOwnIncidents,
  qualifyDegradations,
  rachatDepotOrchestrateur,
  signOrchestratorIncidentPhotos,
} from "../lib/incidents-api"
import { fetchPoolPrenoms } from "../lib/pool-prenoms"
import ProprioPhotoViewer from "./proprio/ProprioPhotoViewer"
import { firstNameFromEmail } from "./menage/RunCard"
import { resolveLogementNom } from "../lib/sousloc-helpers"
// Icônes trait fin (lucide-react direct — DESIGN.md §5, PAS via lib/Icon.jsx).
import {
  AlertTriangle, Search, ShoppingCart, CircleCheck, Home, User,
  Recycle, Trash2, CalendarDays,
} from "lucide-react"

// Métadonnées de statut — UN SEUL MOT PAR ÉTAT (Dimitri 2026-08-21) :
// « À juger » (l'ancien « À vérifier » désignait le même état), « Réglé ».
const STATUS_META = {
  [STATUS.EN_COURS]:   { label: 'À juger',    Ico: Search,       bg: DS.warningBg, fg: DS.warningText },
  [STATUS.A_RACHETER]: { label: 'À racheter', Ico: ShoppingCart, bg: DS.dangerBg,  fg: DS.dangerText },
  [STATUS.RESOLU]:     { label: 'Réglé',      Ico: CircleCheck,  bg: DS.successBg, fg: DS.successText },
}

// L'EN-TÊTE DE CARTE PREND LA COULEUR DE L'ÉTAT (maquette 2026-08-21).
const TEINTES_ETAT = {
  [STATUS.EN_COURS]:   { grad: 'linear-gradient(100deg,#fdf7ee,#fdfbf6)', bord: '#e8d4b0', pastille: '#d4a24c', Ico: Search },
  [STATUS.A_RACHETER]: { grad: 'linear-gradient(100deg,#fdf4f0,#fdfaf8)', bord: '#e0b5a5', pastille: '#b8412b', Ico: ShoppingCart },
  [STATUS.RESOLU]:     { grad: 'linear-gradient(100deg,#f7fbf9,#fbfdfc)', bord: '#c9e0d4', pastille: '#7fc4a3', Ico: CircleCheck },
}

// LE TRAVAIL DE L'ORCHESTRATEUR EST-IL TERMINÉ ? (règle Dimitri 2026-08-21)
// Un incident « à racheter » dont TOUS les articles non récupérables sont
// rachetés ET déposés PAR LUI quitte SA file et rejoint SES « Réglés » — état
// d'AFFICHAGE seulement : le status base ne bouge pas (clôture = admin), et
// chez l'admin comme chez le proprio l'incident reste ouvert. Les booléens
// racheteParMoi / deposeParMoi viennent du serveur (le nom, jamais).
// Si c'est L'ADMIN qui a racheté ou déposé (parMoi false), l'incident RESTE
// dans sa file : il voit que ça avance sans lui (étapes datées), c'est tout.
export function travailOrchestrateurTermine(incident) {
  if (normalizeStatus(incident?.status) !== STATUS.A_RACHETER) return false
  const degs = Array.isArray(incident.degradations) ? incident.degradations : []
  const nonRecup = degs.filter(d => d && d.statutMenage === 'Non récupérable')
  if (!nonRecup.length) return false
  return nonRecup.every(d =>
    d.racheteLe && d.remplaceLe && d.racheteParMoi === true && d.deposeParMoi === true)
}

// « août 2026 » — le mois du bandeau (flèches comme le Bilan du contrôle).
function libelleMoisFR(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  if (Number.isNaN(t)) return month
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' }).format(t)
}
function moisVoisin(month, pas) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + pas, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

// « 12 août » — LE format de date de l'écran (règle du dessin unique appliquée
// aux dates, tri Dimitri 2026-08-21) : jamais de jour de semaine, jamais
// d'année, partout pareil (contexte, étapes, horodatages).
function dateCourteFR(iso) {
  if (typeof iso !== 'string' || iso.length < 10) return null
  const t = Date.parse(`${iso.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return null
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}

// « 9 août à 17h30 » — la variante horodatée, heure FRANÇAISE (jamais 17:30).
function dateHeureCourteFR(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const jour = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(d)
  const h = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' })
    .format(d).replace(':', 'h')
  return `${jour} à ${h}`
}

export default function MenageIncidentsView({ basculePage = null, cfg, currentUser }) {
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // busy a disparu avec l'optimisme (L-a) : un geste fait disparaître son
  // bouton immédiatement — le double-clic est impossible par construction.
  // LE FILTRE DU BANDEAU (orchestrateur) : null = tout, sinon un statut canonique.
  const [filtre, setFiltre] = useState(null)
  // LE MOIS DES RÉGLÉS (règle 2026-08-21) — ouvre sur le courant, JAMAIS
  // mémorisé ; l'attente (à juger, à racheter) arrive toujours, tous mois.
  const moisCourant = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' })
    .format(new Date()).slice(0, 7)
  const [mois, setMois] = useState(moisCourant)
  // Prénoms SAISIS du pool (orchestrateur) — filet libelleDepuisEmail derrière.
  const [prenoms, setPrenoms] = useState({})

  // Permissions 42 Phase 3 Lot 5 — MODE POOL pour l'orchestrateur : il qualifie le
  // STATUT des incidents de SON pool (lecture via endpoint serveur, jamais un SELECT
  // client brut — la RLS n'a aucune policy SELECT pool). L'agent 'menage' simple
  // garde son comportement historique (SES incidents, sens unique, RLS menage_select_own).
  const isOrchestrateur = currentUser?.role === 'orchestrateur'

  // Rechargement SILENCIEUX (L-a, tri Dimitri 2026-08-21) : après une action,
  // on remplace la liste SANS repasser par « Chargement… » — l'écran ne
  // clignote plus. Le voile ne sert qu'au premier chargement.
  const rechargerSilencieux = useCallback(async () => {
    try {
      const list = await (isOrchestrateur
        ? fetchOrchestratorPoolIncidents({ limit: 200, month: mois })
        : fetchOwnIncidents({ limit: 200 }))
      setIncidents(list)
    } catch (e) { setError(e.message || String(e)) }
  }, [isOrchestrateur, mois])

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      if (isOrchestrateur) {
        const list = await fetchOrchestratorPoolIncidents({ limit: 200, month: mois })
        setIncidents(list)
      } else {
        // AUDIT COMPTES 2026-07 (verrou argent ADR-227) : l'agent lit SES
        // incidents via l'endpoint serveur `menage_incidents_own` (whitelist
        // POSITIVE de colonnes — plus jamais admin_note / action_taken ni les
        // sous-champs argent de degradations), jamais un SELECT client direct.
        const list = await fetchOwnIncidents({ limit: 200 })
        setIncidents(list)
      }
    } catch (e) { setError(e.message || String(e)) }
    finally { setLoading(false) }
    // currentUser.email retiré des deps : le cloisonnement self est désormais
    // serveur (email du JWT), le callback ne le lit plus.
  }, [isOrchestrateur, mois])

  useEffect(() => { reload() }, [reload])

  // Prénoms du pool — « ménage de Sofia » sur la ligne de contexte. Cache 5 min
  // côté client ; {} sur erreur (le filet dérive de l'email, C-1).
  useEffect(() => {
    if (!isOrchestrateur) return
    let vivant = true
    fetchPoolPrenoms().then((p) => { if (vivant) setPrenoms(p || {}) })
    return () => { vivant = false }
  }, [isOrchestrateur])

  const prenomDe = useCallback(
    (email) => prenoms[email] || firstNameFromEmail(email) || 'Agent',
    [prenoms],
  )

  // Date du jour (Europe/Paris), calculée UNE fois et injectée dans les helpers
  // purs — ils ne consultent jamais l'horloge eux-mêmes.
  const aujourdHui = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()),
    [],
  )

  // REFONTE 2026-08-13 — LA PAGE EST EN GROUPES, PLUS EN ONGLETS (décision
  // Dimitri) : tout se voit d'un regard, l'urgent d'abord. « À juger » = file
  // d'attente, PLUS ANCIEN EN TÊTE ; les deux autres groupes sont des archives.
  // CLASSEMENT D'AFFICHAGE : chez l'orchestrateur, un incident dont il a tout
  // racheté et déposé rejoint SES « Réglés » (travailOrchestrateurTermine) —
  // le status base reste a_racheter jusqu'à la clôture admin.
  const cleAffichage = useCallback((i) => {
    const s = normalizeStatus(i.status)
    if (isOrchestrateur && s === STATUS.A_RACHETER && travailOrchestrateurTermine(i)) return STATUS.RESOLU
    return s
  }, [isOrchestrateur])

  const groupes = useMemo(() => ([
    { cle: STATUS.EN_COURS, titre: 'À juger',
      liste: trierParAnciennete(incidents.filter(i => cleAffichage(i) === STATUS.EN_COURS)) },
    { cle: STATUS.A_RACHETER, titre: 'À racheter',
      liste: incidents.filter(i => cleAffichage(i) === STATUS.A_RACHETER) },
    { cle: STATUS.RESOLU, titre: 'Réglés',
      liste: incidents.filter(i => cleAffichage(i) === STATUS.RESOLU) },
  ]), [incidents, cleAffichage])

  // Phrase de contrôle — AGENT SEULEMENT (chez l'orchestrateur, l'information
  // vit dans les blocs du bandeau depuis la maquette 2026-08-21).
  const resume = useMemo(
    () => resumeAttente(incidents.filter(i => normalizeStatus(i.status) === STATUS.EN_COURS), aujourdHui),
    [incidents, aujourdHui],
  )

  // ADR-228 — qualification PAR DÉGRADATION (orchestrateur), depuis « à juger »
  // seulement. On envoie l'ENSEMBLE des statuts courants des dégradations (idempotent
  // et plus simple qu'un diff) via l'endpoint serveur (qualifyDegradations, scope
  // pool ∧ logIds re-vérifié frais, status DÉRIVÉ serveur). L'UI n'a pas à deviner
  // le status global : elle recharge. L'agent ménage n'a AUCUNE case de jugement.
  // Pose LOCALE d'un champ de dégradation (optimisme + annulation).
  const majDegLocale = useCallback((incidentId, degId, champs) => {
    setIncidents(prev => prev.map(i => i.id !== incidentId ? i : {
      ...i,
      degradations: (i.degradations || []).map(d =>
        d && d.id === degId ? { ...d, ...champs } : d),
    }))
  }, [])

  // OPTIMISTE (L-a) : l'état s'écrit TOUT DE SUITE (les boutons cèdent la
  // place au mot), le serveur confirme en silence — on ANNULE s'il refuse.
  // Même motif que les cases de reprise de l'agent (ReprisesAgent).
  async function handleQualifyDeg(incident, degId, statut) {
    const degs = Array.isArray(incident.degradations) ? incident.degradations : []
    const statuts = {}
    degs.forEach(d => {
      if (!d || !d.id) return
      statuts[d.id] = d.id === degId ? statut : (d.statutMenage || "À vérifier")
    })
    if (Object.keys(statuts).length === 0) return
    const avant = degs.find(d => d && d.id === degId)?.statutMenage || "À vérifier"
    setError(null)
    majDegLocale(incident.id, degId, { statutMenage: statut })
    try {
      await qualifyDegradations(incident.id, statuts)
      // Le status global est DÉRIVÉ SERVEUR : la relecture silencieuse fait
      // changer l'incident de groupe sans faire disparaître l'écran.
      await rechargerSilencieux()
      window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
    } catch (e) {
      majDegLocale(incident.id, degId, { statutMenage: avant })
      // Le serveur refuse désormais toute clé qu'il ne connaît pas (409
      // `degradation_inconnue`, plus jamais un no-op à 200) : une dégradation
      // retirée par un admin pendant qu'on juge. On recharge et on le dit en
      // français — jamais le code brut à l'écran.
      if (e && e.code === 'degradation_inconnue') {
        await rechargerSilencieux()
        setError("L'incident a changé entre-temps : une de ses dégradations n'existe plus. Je l'ai rechargé, rien n'a été enregistré.")
        return
      }
      setError(e.message || String(e))
    }
  }

  // RACHAT / DÉPÔT par l'orchestrateur — deux dates, jamais un montant. La
  // trace (qui) est posée par le SERVEUR ; l'écran n'affiche d'ailleurs JAMAIS
  // un nom ici (strip serveur deposePar/rachetePar).
  async function handleGeste(incident, degId, geste, date) {
    const degs = Array.isArray(incident.degradations) ? incident.degradations : []
    const avant = degs.find(d => d && d.id === degId) || {}
    setError(null)
    // OPTIMISTE : la date se pose tout de suite (le bouton disparaît, l'étape
    // s'écrit — et « tout fait par lui » bascule sans attendre le serveur).
    majDegLocale(incident.id, degId, geste === 'rachat'
      ? { racheteLe: date, racheteParMoi: true }
      : { remplaceLe: date, deposeParMoi: true })
    try {
      await rachatDepotOrchestrateur(incident.id, degId, geste, date)
      await rechargerSilencieux()
      window.dispatchEvent(new Event('fbz:orchestrateur-badges'))
    } catch (e) {
      majDegLocale(incident.id, degId, geste === 'rachat'
        ? { racheteLe: avant.racheteLe ?? null, racheteParMoi: avant.racheteParMoi }
        : { remplaceLe: avant.remplaceLe ?? null, deposeParMoi: avant.deposeParMoi })
      setError(e.message || String(e))
    }
  }

  // Helper lookup nom logement depuis cfg — extraite dans sousloc-helpers pour
  // être partagée avec l'onglet Incidents admin. Comportement inchangé.
  const logName = (logementId) => resolveLogementNom(cfg, logementId)

  // ═══ LE BANDEAU DE L'ORCHESTRATEUR — il sert, il ne décore pas ═══
  const bandeauOrch = (() => {
    if (!isOrchestrateur) return null
    const aJuger = groupes[0].liste
    const aRacheter = groupes[1].liste
    const regles = groupes[2].liste
    const joursAJuger = aJuger.map(i => joursDAttente(i, aujourdHui)).filter(j => j != null)
    const plusVieux = joursAJuger.length ? Math.max(...joursAJuger) : null
    // « 2 articles » — le travail restant du groupe à racheter : les articles
    // non récupérables pas encore déposés.
    const nbArticles = aRacheter.reduce((n, inc) => {
      const degs = Array.isArray(inc.degradations) ? inc.degradations : []
      return n + degs.filter(d => d && d.statutMenage === 'Non récupérable' && !d.remplaceLe).length
    }, 0)
    const blocs = [
      { cle: STATUS.EN_COURS, fond: '#d4a24c', Ico: Search,
        compte: `${aJuger.length} à juger`,
        precision: aJuger.length === 0 ? null
          : plusVieux == null ? null
          : plusVieux === 0 ? "tous signalés aujourd'hui"
          : `le plus ancien depuis ${plusVieux} j`,
        couleurPrecision: '#f5d9a8' },
      { cle: STATUS.A_RACHETER, fond: '#b8412b', Ico: ShoppingCart,
        compte: `${aRacheter.length} à racheter`,
        precision: nbArticles > 0 ? `${nbArticles} article${nbArticles > 1 ? 's' : ''}` : null,
        couleurPrecision: 'rgba(255,255,255,.75)' },
      { cle: STATUS.RESOLU, fond: '#7fc4a3', Ico: CircleCheck,
        compte: `${regles.length} réglé${regles.length > 1 ? 's' : ''}`,
        precision: null, couleurPrecision: 'rgba(255,255,255,.75)' },
    ]
    return (
      <CarteTitrePage
        testId="incidents-bandeau"
        titre={(
          <span style={{ fontSize: 20 }}>
            Incidents{' '}
            <span data-testid="incidents-mois" style={{ fontSize: 11.5, fontWeight: 400, fontFamily: DS.fontBody, color: 'rgba(255,255,255,.75)' }}>
              {libelleMoisFR(mois)}
            </span>
          </span>
        )}
        actions={(
          <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
            <button type="button" data-testid="incidents-mois-prec" aria-label="Mois précédent"
              onClick={() => setMois((m) => moisVoisin(m, -1))} className="fbz-press"
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700 }}>‹</button>
            <button type="button" data-testid="incidents-mois-suiv" aria-label="Mois suivant"
              onClick={() => setMois((m) => moisVoisin(m, 1))} disabled={mois >= moisCourant}
              className="fbz-press"
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none',
                cursor: mois >= moisCourant ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700,
                opacity: mois >= moisCourant ? .45 : 1 }}>›</button>
          </span>
        )}
        sousLeFilet={(
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap' }}>
            {blocs.map(({ cle, fond, Ico, compte, precision, couleurPrecision }) => {
              const actif = filtre === cle
              return (
                <button key={cle} type="button" className="fbz-press"
                  data-testid={`incidents-filtre-${cle}`}
                  aria-pressed={actif}
                  onClick={() => setFiltre(f => (f === cle ? null : cle))}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 44,
                    padding: '6px 12px', borderRadius: 10, border: 'none',
                    background: actif ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.13)',
                    boxShadow: actif ? 'inset 0 0 0 1px rgba(255,255,255,.3)' : 'none',
                    cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody }}>
                  <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9,
                    background: fond, flexShrink: 0, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center' }}>
                    <Ico size={16} strokeWidth={2.2} color="#fff" />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                      {compte}
                    </span>
                    {precision && (
                      <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: couleurPrecision }}>
                        {precision}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        )}
      />
    )
  })()

  // Le filtre du bandeau ne montre que son groupe ; sans filtre, tout.
  const groupesAffiches = filtre ? groupes.filter(g => g.cle === filtre) : groupes
  // La raison d'une absence s'écrit (règle des poses) — par groupe filtré vide.
  const VIDE_FILTRE = {
    [STATUS.EN_COURS]: 'Rien à juger.',
    [STATUS.A_RACHETER]: 'Rien à racheter.',
    [STATUS.RESOLU]: `Aucun incident réglé en ${libelleMoisFR(mois)}.`,
  }

  return (
    // PLEINE LARGEUR (Dimitri 2026-08-21) : plus de plafond local — le bandeau
    // fait la largeur commune de la zone de contenu, comme Planning et Demandes.
    <div style={{padding:'0 0 80px',fontFamily:DS.fontBody,color:DS.marineDark}}>
      {isOrchestrateur ? bandeauOrch : (
        <CarteTitrePage
          titre="Incidents"
          /* Sans son propre prénom : « Mes incidents » dit déjà que ce sont
             les siens (règle « jamais sa propre identité », 2026-08-23). */
          sousTitre="Mes incidents" />
      )}
      {basculePage}

      {/* Phrase de contrôle — AGENT seulement (l'orchestrateur a les blocs). */}
      {!isOrchestrateur && (
        <div data-testid="incidents-resume"
          style={{fontSize:12.5,marginTop:-6,marginBottom:14,
            color: resume.alerte ? DS.dangerText : DS.textSecondary,
            fontWeight: resume.alerte ? 600 : 400}}>
          {resume.texte}
        </div>
      )}

      {loading && <div style={{padding:32,textAlign:'center',color:DS.textMuted,fontSize:13}}>Chargement…</div>}
      {error && (
        <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:14,background:DS.dangerBg,color:DS.dangerText,borderRadius:10,fontSize:13,marginBottom:10}}>
          <AlertTriangle size={16} strokeWidth={2} style={{flexShrink:0,marginTop:1}} /> {error}
        </div>
      )}

      {!loading && !error && incidents.length === 0 && !isOrchestrateur && (
        <div style={{padding:40,textAlign:'center',color:DS.textMuted,fontSize:13,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:12}}>
          Aucun incident. Le signalement se fait depuis la carte du ménage, le jour même ou dans les 48 h.
        </div>
      )}
      {!loading && !error && incidents.length === 0 && isOrchestrateur && (
        <>
          <div style={{padding:'26px 16px',textAlign:'center',color:DS.textMuted,fontSize:13,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:12,marginBottom:12}}>
            Rien à juger ni à racheter — l'attente est vide, tous mois confondus.
          </div>
          <div data-testid="incidents-regles-vide"
            style={{padding:20,textAlign:'center',color:DS.textMuted,fontSize:12.5,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:12}}>
            Aucun incident réglé en {libelleMoisFR(mois)}.
          </div>
        </>
      )}

      {/* ═══ LES GROUPES — l'urgent d'abord, tout d'un regard ═══ */}
      {!loading && !error && (incidents.length > 0) && groupesAffiches.map(({ cle, titre, liste }) => {
        if (!liste.length) {
          // RÉGLÉS : un mois sans incident réglé LE DIT, filtre ou pas
          // (l'attente, elle, ne s'affiche vide que filtrée).
          if (isOrchestrateur && cle === STATUS.RESOLU && !filtre) {
            return (
              <div key={cle} data-testid="incidents-regles-vide"
                style={{padding:20,textAlign:'center',color:DS.textMuted,fontSize:12.5,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:12,marginBottom:18}}>
                Aucun incident réglé en {libelleMoisFR(mois)}.
              </div>
            )
          }
          if (!filtre) return null
          return (
            <div key={cle} data-testid={`incidents-vide-${cle}`}
              style={{padding:28,textAlign:'center',color:DS.textMuted,fontSize:13,background:DS.card,border:`0.5px solid ${DS.border}`,borderRadius:12}}>
              {VIDE_FILTRE[cle]}
            </div>
          )
        }
        const meta = STATUS_META[cle]
        return (
          <section key={cle} data-testid={`incidents-groupe-${cle}`} style={{marginBottom:18}}>
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'5px 2px',marginBottom:8}}>
              <meta.Ico size={13} strokeWidth={2.2} color={meta.fg} />
              <span style={{fontSize:11,fontWeight:700,color:meta.fg,textTransform:'uppercase',letterSpacing:'.07em'}}>
                {titre} · {liste.length}
              </span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              {liste.map(inc => (
                isOrchestrateur ? (
                  <IncidentCardOrch
                    key={inc.id}
                    incident={inc}
                    logName={logName(inc.logement_id)}
                    prenomDe={prenomDe}
                    onQualifyDeg={(degId, statut) => handleQualifyDeg(inc, degId, statut)}
                    onGeste={(degId, geste, date) => handleGeste(inc, degId, geste, date)}
                    aujourdHui={aujourdHui}
                    enAttente={cle === STATUS.EN_COURS}
                  />
                ) : (
                  <IncidentCardAgent
                    key={inc.id}
                    incident={inc}
                    logName={logName(inc.logement_id)}
                  />
                )
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════
// LA CARTE DE L'ORCHESTRATEUR — maquette validée 2026-08-21.
// ═════════════════════════════════════════════════════════════════════

function IncidentCardOrch({ incident, logName, prenomDe, onQualifyDeg, onGeste, aujourdHui, enAttente }) {
  const statut = normalizeStatus(incident.status)
  // « Tout racheté et déposé PAR LUI » : la carte prend le DESSIN du Réglé et
  // dit pourquoi (sinon il croira que l'admin a fermé) — le status base, lui,
  // reste a_racheter jusqu'à la clôture admin.
  const attendCloture = travailOrchestrateurTermine(incident)
  const statutAffiche = attendCloture ? STATUS.RESOLU : statut
  const teinte = TEINTES_ETAT[statutAffiche] || TEINTES_ETAT[STATUS.EN_COURS]
  const degs = Array.isArray(incident.degradations) ? incident.degradations.filter(Boolean) : []

  // LES VIGNETTES — signées au montage (c'est sur les photos qu'on juge).
  // 1 appel par incident à photos ; [] sur échec (pas d'invitation au vide).
  const [photos, setPhotos] = useState([])
  const [viewer, setViewer] = useState(null) // { photos, index } | null
  const photosCount = incident.photos_count || 0
  useEffect(() => {
    if (!photosCount) return
    let vivant = true
    signOrchestratorIncidentPhotos(incident.id).then((p) => { if (vivant) setPhotos(p) })
    return () => { vivant = false }
  }, [incident.id, photosCount])

  // « Claire Dubois · ménage de Sofia · 12 août » — le contexte sur UNE ligne.
  const contexte = [
    incident.casse_interne ? 'Casse interne' : (incident.voyageur || null),
    incident.reported_by ? `ménage de ${prenomDe(incident.reported_by)}` : null,
    dateCourteFR(incident.incident_date || incident.reported_at || incident.created_at),
  ].filter(Boolean).join(' · ')

  const jAttente = enAttente ? joursDAttente(incident, aujourdHui) : null
  const tard = enAttente && attenteAnormale(incident, aujourdHui)
  const visibles = photos.slice(0, 3)
  const surplus = photos.length - visibles.length

  return (
    <article className="fbz-card" style={{background:DS.card,borderRadius:14,border:`1px solid ${teinte.bord}`,boxShadow:'0 1px 2px rgba(33,83,135,.05)',overflow:'hidden'}}>
      {/* ── L'EN-TÊTE AUX COULEURS DE L'ÉTAT ── */}
      <div data-testid={`incident-entete-${incident.id}`} data-etat={statutAffiche}
        style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',
          background:teinte.grad,borderBottom:`1px solid ${teinte.bord}`}}>
        <span aria-hidden="true" style={{width:34,height:34,borderRadius:10,background:teinte.pastille,
          flexShrink:0,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
          <teinte.Ico size={17} strokeWidth={2.2} color="#fff" />
        </span>
        <span style={{flex:1,minWidth:0}}>
          <span style={{display:'block',fontSize:15,fontWeight:700,color:'#233240',
            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {logName}
          </span>
          {contexte && (
            <span data-testid={`incident-contexte-${incident.id}`}
              style={{display:'block',fontSize:11,marginTop:1,color:'#5c6773',
                overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {contexte}
            </span>
          )}
        </span>
        {/* L'ancienneté — file de jugement seulement (doctrine 2026-08-06 :
            sur une archive, un compteur est un reproche sans objet). */}
        {jAttente != null && (
          <span data-testid={`attente-${incident.id}`} data-tard={tard ? '1' : '0'}
            style={{flexShrink:0,fontSize:11,fontWeight:tard ? 700 : 600,
              padding:'3px 8px',borderRadius:7,background:'#fff',
              border:`1px solid ${tard ? DS.danger : teinte.bord}`,
              color:tard ? DS.dangerText : '#233240'}}>
            {/* AU DOIGT, LE SENS S'ÉCRIT (cadrage mobile 2026-08-22) : l'infobulle
                qui distinguait « à juger depuis » de « signalé il y a » est morte
                sur téléphone — le libellé porte le sens, en toutes lettres. */}
            {jAttente === 0 ? "aujourd'hui"
              : tard ? `à juger depuis ${jAttente} j` : `il y a ${jAttente} j`}
          </span>
        )}
      </div>

      {attendCloture && (
        <div data-testid={`attente-cloture-${incident.id}`}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',
            background:'#f7fbf9',borderBottom:'1px solid #c9e0d4',
            fontSize:11.5,fontWeight:600,color:'#1f5a43'}}>
          <CircleCheck size={13} strokeWidth={2.2} color="#7fc4a3" />
          vous avez tout racheté et déposé · en attente de clôture
        </div>
      )}

      <div style={{padding:'12px 14px'}}>
        {/* ── LES VIGNETTES — 48px, la 3e porte le surplus sous voile ── */}
        {visibles.length > 0 && (
          <div style={{display:'flex',gap:6,marginBottom:10}}>
            {visibles.map((p, i) => (
              <button key={p.fileName || i} type="button" className="fbz-press"
                data-testid={`incident-vignette-${incident.id}-${i}`}
                aria-label={`Photo ${i + 1} sur ${photos.length}`}
                onClick={() => setViewer({ photos, index: i })}
                style={{position:'relative',width:48,height:48,borderRadius:9,
                  border:'1px solid #d5e3ee',padding:0,overflow:'hidden',
                  cursor:'pointer',background:DS.toolBg,flexShrink:0}}>
                <img src={p.signedUrl} alt=""
                  style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                {i === 2 && surplus > 0 && (
                  <span data-testid={`incident-vignette-surplus-${incident.id}`}
                    style={{position:'absolute',inset:0,background:'rgba(35,50,64,.55)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      color:'#fff',fontWeight:700,fontSize:13}}>
                    +{surplus}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── UNE LIGNE PAR ARTICLE ── */}
        {degs.length > 0 ? (
          <div>
            {degs.map((d, i) => (
              <LigneArticle key={d.id || i} deg={d} incident={incident} statut={statut}
                onQualifyDeg={onQualifyDeg} onGeste={onGeste}
                aujourdHui={aujourdHui} derniere={i === degs.length - 1} />
            ))}
          </div>
        ) : incident.description ? (
          <div style={{fontSize:12,color:DS.textSecondary,fontStyle:'italic'}}>
            {incident.description}
          </div>
        ) : null}

        {/* F-a (ceinture, tri Dimitri 2026-08-21) : la note admin n'a PLUS de
            chemin de rendu ici — le serveur ne la sert jamais (liste blanche),
            et même une fuite ne s'afficherait plus. */}

        {/* Horodatages — C-1 (prénom dérivé) + UN SEUL format de date. */}
        {(incident.recovered_at || incident.bought_at) && (
          <div style={{padding:'8px 12px',background:DS.toolBg,borderRadius:8,marginTop:10,fontSize:11,color:DS.textSecondary,lineHeight:1.6}}>
            {incident.recovered_at && (
              <div style={{display:'flex',alignItems:'center',gap:5}}><CircleCheck size={12} strokeWidth={2} color={DS.success} /> Récupéré le {dateHeureCourteFR(incident.recovered_at)}{incident.recovered_by ? ` par ${firstNameFromEmail(incident.recovered_by)}` : ''}</div>
            )}
            {incident.bought_at && (
              <div style={{display:'flex',alignItems:'center',gap:5}}><ShoppingCart size={12} strokeWidth={2} color={DS.danger} /> Racheté le {incident.bought_date ? dateCourteFR(incident.bought_date) : dateCourteFR(incident.bought_at)}{incident.bought_by ? ` (saisi par ${firstNameFromEmail(incident.bought_by)})` : ''}</div>
            )}
          </div>
        )}
      </div>

      {/* Visionneuse plein écran — photos SIGNÉES. onExpired re-signe LE SEUL
          incident ouvert ; si plus rien de signable, on ferme. */}
      {viewer && (
        <ProprioPhotoViewer
          photos={viewer.photos}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onIndex={(i) => setViewer(v => (v ? { ...v, index: i } : v))}
          onExpired={async () => {
            const fresh = await signOrchestratorIncidentPhotos(incident.id)
            setPhotos(fresh)
            setViewer(v => (fresh.length ? { ...v, photos: fresh } : null))
          }}
        />
      )}
    </article>
  )
}

// ── UNE LIGNE PAR ARTICLE — puce, libellé, et le jugement à droite ──────────
function LigneArticle({ deg, incident, statut, onQualifyDeg, onGeste, aujourdHui, derniere }) {
  const juge = deg.statutMenage && deg.statutMenage !== 'À vérifier'
  const nombre = deg.nombre && deg.nombre !== '1' ? ` × ${deg.nombre}` : ''
  return (
    <div data-testid={`article-${deg.id || 'x'}`}
      style={{padding:'8px 0',borderBottom:derniere ? 'none' : '1px solid #f2f4f6'}}>
      <div style={{display:'flex',alignItems:'center',gap:9}}>
        <span aria-hidden="true" style={{width:8,height:8,borderRadius:'50%',background:'#b8412b',flexShrink:0}} />
        <span style={{flex:1,minWidth:0}}>
          <span style={{display:'block',fontSize:12.5,fontWeight:600,color:DS.textPrimary}}>
            {deg.type}{nombre}
          </span>
          {deg.precision && (
            <span style={{display:'block',fontSize:11,fontStyle:'italic',color:'#a8664f'}}>
              {deg.precision}
            </span>
          )}
        </span>
        {/* À droite : les boutons TANT QUE l'article n'est pas jugé — puis son
            état EN TOUTES LETTRES (un jugement engage un rachat, il doit se
            VOIR, pas se deviner). */}
        {statut === STATUS.EN_COURS && deg.id && !juge ? (
          <span style={{display:'inline-flex',gap:5,flexShrink:0}}>
            <BoutonJugement mot="Récupérable" fond="#f0f7f3" couleur="#1f5a43" Ico={Recycle}
              onChoisir={() => onQualifyDeg(deg.id, 'Récupérable')} />
            <BoutonJugement mot="Non récupérable" fond="#fdeae4" couleur="#b8412b" Ico={Trash2}
              onChoisir={() => onQualifyDeg(deg.id, 'Non récupérable')} />
          </span>
        ) : (
          <EtapeArticle deg={deg} />
        )}
      </div>
      {/* GESTES rachat/dépôt : article non récupérable pas encore déposé, d'un
          incident jugé « à racheter ». Deux dates, jamais un montant. */}
      {statut === STATUS.A_RACHETER && deg.id && onGeste
        && deg.statutMenage === 'Non récupérable' && !deg.remplaceLe && (
        <div style={{paddingLeft:17}}>
          <GesteArticle deg={deg} aujourdHui={aujourdHui}
            onGeste={(geste, date) => onGeste(deg.id, geste, date)} />
        </div>
      )}
    </div>
  )
}

// ── LE BOUTON DE JUGEMENT — carré 42px, icône nue, APPUI LONG = le mot ──────
// La parade contre l'erreur (maquette 2026-08-21) : l'appui long affiche une
// bulle avec le mot, et NE déclenche PAS le jugement (le clic qui suit un
// appui long est avalé). title + aria-label portent le mot en permanence.
function BoutonJugement({ mot, fond, couleur, Ico, busy = false, onChoisir }) {
  const [bulle, setBulle] = useState(false)
  const timerRef = useRef(null)
  const longRef = useRef(false)
  const debut = () => {
    longRef.current = false
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { longRef.current = true; setBulle(true) }, 470)
  }
  const fin = () => {
    clearTimeout(timerRef.current)
    if (longRef.current) setTimeout(() => setBulle(false), 900)
  }
  useEffect(() => () => clearTimeout(timerRef.current), [])
  return (
    <span style={{position:'relative',display:'inline-flex'}}>
      {bulle && (
        <span data-testid="bulle-jugement" role="tooltip"
          style={{position:'absolute',bottom:46,right:0,background:'#233240',color:'#fff',
            fontSize:10.5,fontWeight:600,padding:'4px 8px',borderRadius:7,
            whiteSpace:'nowrap',zIndex:5,pointerEvents:'none'}}>
          {mot}
        </span>
      )}
      <button type="button" className="fbz-press" disabled={busy}
        title={mot} aria-label={mot}
        onPointerDown={debut} onPointerUp={fin} onPointerLeave={fin} onPointerCancel={fin}
        onContextMenu={(e) => e.preventDefault()}
        onClick={() => {
          if (longRef.current) { longRef.current = false; return }
          onChoisir()
        }}
        style={{width:42,height:42,borderRadius:9,border:'none',background:fond,
          display:'inline-flex',alignItems:'center',justifyContent:'center',
          cursor:busy ? 'not-allowed' : 'pointer',opacity:busy ? .55 : 1}}>
        <Ico size={18} strokeWidth={2.1} color={couleur} />
      </button>
    </span>
  )
}

// ═════════════════════════════════════════════════════════════════════
// LA CARTE DE L'AGENT — dessin historique (lecture seule, sera recalée
// avec l'espace MÉNAGE). C-1 appliqué aux horodatages (2026-08-21).
// ═════════════════════════════════════════════════════════════════════

function IncidentCardAgent({ incident, logName }) {
  const sc = STATUS_META[normalizeStatus(incident.status)] || STATUS_META[STATUS.EN_COURS]
  const degs = Array.isArray(incident.degradations) ? incident.degradations.filter(Boolean) : []

  return (
    <article className="fbz-card" style={{background:DS.card,borderRadius:14,border:`0.5px solid ${DS.border}`,boxShadow:'0 1px 2px rgba(33,83,135,.05)',padding:'14px 16px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7,flexWrap:'wrap'}}>
        <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,padding:'4px 9px',borderRadius:7,background:sc.bg,color:sc.fg,fontWeight:700}}>
          <sc.Ico size={13} strokeWidth={2.1} /> {sc.label}
        </span>
        <span style={{fontSize:11,color:DS.textMuted}}>
          {incident.incident_date ? formatDateFR(incident.incident_date) : (incident.reported_at ? formatDateFR(incident.reported_at) : '—')}
        </span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:7,fontFamily:DS.fontDisplay,fontSize:16,fontWeight:600,color:DS.marine,marginBottom:5}}>
        <Home size={16} strokeWidth={2} color={DS.ciel} /> {logName}
      </div>
      {incident.casse_interne ? (
        <div data-testid={`incident-casse-interne-${incident.id}`}
          style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,padding:'3px 8px',borderRadius:7,background:DS.toolBg,color:DS.textSecondary,fontWeight:700,marginBottom:6}}>
          Casse interne — aucun voyageur en cause
        </div>
      ) : incident.voyageur ? (
        <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:DS.textSecondary,marginBottom:6}}>
          <User size={13} strokeWidth={2} color={DS.ciel} /> {incident.voyageur}
        </div>
      ) : null}

      {degs.length > 0 ? (
        <div style={{padding:'10px 12px',background:DS.dangerBg,borderRadius:8,marginTop:8,marginBottom:8}}>
          {degs.map((d, i) => (
            <div key={d.id || i} style={{fontSize:12,color:DS.textPrimary,marginBottom: i < degs.length - 1 ? 8 : 0}}>
              <span style={{background:DS.card,color:DS.dangerText,padding:'2px 7px',borderRadius:5,fontWeight:700,fontSize:10,marginRight:6}}>
                {d.type}
              </span>
              {d.nombre && d.nombre !== '1' && <span style={{fontWeight:700}}>×{d.nombre}</span>}
              {d.precision && <span style={{fontStyle:'italic',color:DS.textSecondary}}> — {d.precision}</span>}
              <EtapeArticle deg={d} />
            </div>
          ))}
        </div>
      ) : incident.description ? (
        <div style={{fontSize:12,color:DS.textSecondary,marginTop:6,marginBottom:6,fontStyle:'italic'}}>
          {incident.description}
        </div>
      ) : null}

      {/* F-a (2026-08-21) : plus de chemin de rendu pour la note admin — le
          serveur ne la sert jamais à l'agent (audit comptes 2026-07). */}

      {/* Horodatages — C-1 : prénom dérivé, jamais l'identifiant brut. */}
      {(incident.recovered_at || incident.bought_at) && (
        <div style={{padding:'8px 12px',background:DS.toolBg,borderRadius:8,marginTop:8,fontSize:11,color:DS.textSecondary,lineHeight:1.6}}>
          {incident.recovered_at && (
            <div style={{display:'flex',alignItems:'center',gap:5}}><CircleCheck size={12} strokeWidth={2} color={DS.success} /> Récupéré le {new Date(incident.recovered_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}{incident.recovered_by ? ` par ${firstNameFromEmail(incident.recovered_by)}` : ''}</div>
          )}
          {incident.bought_at && (
            <div style={{display:'flex',alignItems:'center',gap:5}}><ShoppingCart size={12} strokeWidth={2} color={DS.danger} /> Racheté le {incident.bought_date ? formatDateFR(incident.bought_date) : new Date(incident.bought_at).toLocaleDateString('fr-FR')}{incident.bought_by ? ` (saisi par ${firstNameFromEmail(incident.bought_by)})` : ''}</div>
          )}
        </div>
      )}
    </article>
  )
}

// ── LE PIPELINE D'UN ARTICLE — une étape, en toutes lettres ─────────────────
function EtapeArticle({ deg }) {
  let texte = null
  let fond = DS.toolBg
  let couleur = DS.textSecondary
  if (!deg.statutMenage || deg.statutMenage === 'À vérifier') {
    texte = 'à juger'; fond = DS.warningBg; couleur = DS.warningText
  } else if (deg.statutMenage === 'Récupérable') {
    texte = 'récupérable'; fond = DS.successBg; couleur = DS.successText
  } else if (deg.remplaceLe) {
    // « DÉPOSÉ le » — le mot de l'écran (la facture garde son champ). Sans nom.
    texte = `déposé le ${dateCourteFR(deg.remplaceLe)}`; fond = DS.successBg; couleur = DS.successText
  } else if (deg.racheteLe) {
    texte = `racheté le ${dateCourteFR(deg.racheteLe)} — à déposer`; fond = DS.cielSoft; couleur = DS.marine
  } else {
    texte = 'à racheter'; fond = DS.dangerBg; couleur = DS.dangerText
  }
  return (
    <span data-testid={`etape-${deg.id || 'x'}`}
      style={{marginLeft:6,fontSize:10.5,padding:'3px 8px',borderRadius:6,background:fond,color:couleur,fontWeight:700,whiteSpace:'nowrap',flexShrink:0}}>
      {texte}
    </span>
  )
}

// ── LES GESTES DE L'ORCHESTRATEUR — racheter, déposer. Deux dates, 0 montant ─
function GesteArticle({ deg, busy = false, aujourdHui, onGeste }) {
  const [date, setDate] = useState(aujourdHui)
  const dejaRachete = !!deg.racheteLe
  return (
    <div style={{display:'flex',alignItems:'center',gap:6,marginTop:6,flexWrap:'wrap'}}>
      {/* Le natif <input type=date> s'affiche dans la LANGUE DU NAVIGATEUR
          (« 08/21/2026 » en anglais). On force le français : le champ MONTRE
          « 21 août », l'input natif (invisible par-dessus) garde le sélecteur
          et la valeur ISO. */}
      <span style={{position:'relative',display:'inline-flex',alignItems:'center',gap:5,
        border:`0.5px solid ${DS.borderHover}`,borderRadius:9,padding:'7px 10px',
        fontSize:11.5,fontFamily:DS.fontBody,background:DS.card,color:DS.marineDark}}>
        <CalendarDays size={13} strokeWidth={2} color={DS.ciel} aria-hidden="true" />
        <span data-testid={`geste-date-${deg.id}`}>{dateCourteFR(date) || date}</span>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={busy}
          aria-label="Date du geste"
          style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:0,cursor:'pointer'}} />
      </span>
      {!dejaRachete && (
        <button type="button" disabled={busy} className="fbz-press fbz-tap"
          data-testid={`geste-rachat-${deg.id}`}
          onClick={() => onGeste('rachat', date)}
          style={{display:'inline-flex',alignItems:'center',gap:5,padding:'7px 11px',border:'none',borderRadius:9,background:DS.marine,color:'#fff',fontWeight:700,fontSize:11,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
          <ShoppingCart size={12} strokeWidth={2.2}/> Racheté
        </button>
      )}
      <button type="button" disabled={busy} className="fbz-press fbz-tap"
        data-testid={`geste-depot-${deg.id}`}
        onClick={() => onGeste('depot', date)}
        style={{display:'inline-flex',alignItems:'center',gap:5,padding:'7px 11px',border:`1px solid ${DS.border}`,borderRadius:9,background:DS.card,color:DS.marine,fontWeight:700,fontSize:11,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
        <CircleCheck size={12} strokeWidth={2.2}/> Déposé
      </button>
    </div>
  )
}
